import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Chip, Divider } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import JsonDiffViewer from '../JsonDiffViewer';
import ContributorFeedbackModal from '../ContributorFeedbackModal';
import api from '../../services/api';

const TYPE_STYLES = {
  new: { label: 'NEW', color: '#34d399', bg: 'rgba(16, 185, 129, 0.15)' },
  extend: { label: 'EXTEND', color: '#60a5fa', bg: 'rgba(59, 130, 246, 0.15)' },
  merge: { label: 'MERGE', color: '#c084fc', bg: 'rgba(168, 85, 247, 0.15)' },
};

const CONTRIBUTOR_LABELS = {
  primary: { label: 'Primary', color: '#34d399', bg: 'rgba(16, 185, 129, 0.12)' },
  grouped: { label: 'Grouped', color: '#60a5fa', bg: 'rgba(59, 130, 246, 0.12)' },
  inherited: { label: 'Inherited', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.12)' },
  manual: { label: 'Manual', color: '#c084fc', bg: 'rgba(168, 85, 247, 0.12)' },
};

function ScopeChip({ label, color = '#94a3b8', bg = 'rgba(148, 163, 184, 0.12)' }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 22,
        fontSize: '0.68rem',
        fontWeight: 600,
        color,
        background: bg,
        border: `1px solid ${color}40`,
      }}
    />
  );
}

function truncate(text, max = 140) {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function DecisionCard({ decision }) {
  const { type, rule, superseded_rules: supersededRules = [], contributors = [], occurrences = [] } = decision;
  const typeStyle = TYPE_STYLES[(type || '').toLowerCase()] || TYPE_STYLES.new;

  const [contributorModalOpen, setContributorModalOpen] = useState(false);
  const [contributorModalData, setContributorModalData] = useState({
    reviewedBy: null,
    entries: [],
    loading: false,
    error: null,
  });

  const handleContributorClick = async (contributor) => {
    setContributorModalOpen(true);
    setContributorModalData({ reviewedBy: contributor.reviewed_by, entries: [], loading: true, error: null });
    try {
      const data = await api.getContributorFeedback(rule.id, contributor.reviewed_by);
      setContributorModalData({
        reviewedBy: contributor.reviewed_by,
        entries: data.entries || [],
        loading: false,
        error: null,
      });
    } catch (err) {
      setContributorModalData({ reviewedBy: contributor.reviewed_by, entries: [], loading: false, error: err.message });
    }
  };

  return (
    <>
      <Box
        sx={{
          p: 2,
          background: '#0f172a',
          borderLeft: `3px solid ${typeStyle.color}`,
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* Header: type badge + scope chips + active badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip
            label={typeStyle.label}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              color: typeStyle.color,
              background: typeStyle.bg,
              border: `1px solid ${typeStyle.color}40`,
            }}
          />
          {rule.retailer && <ScopeChip label={rule.retailer} color="#2dd4bf" bg="rgba(20, 184, 166, 0.12)" />}
          {rule.attribute_type && <ScopeChip label={rule.attribute_type} color="#818cf8" bg="rgba(99, 102, 241, 0.12)" />}
          {rule.page_type && <ScopeChip label={rule.page_type} color="#fbbf24" bg="rgba(245, 158, 11, 0.12)" />}
          <Box sx={{ flex: 1 }} />
          <Chip
            label={rule.is_active ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.65rem',
              fontWeight: 700,
              color: rule.is_active ? '#34d399' : '#94a3b8',
              background: rule.is_active ? 'rgba(16, 185, 129, 0.12)' : 'rgba(148, 163, 184, 0.12)',
              border: `1px solid ${rule.is_active ? '#34d399' : '#94a3b8'}40`,
            }}
          />
        </Box>

        {/* Rule text */}
        <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.6, mb: 1.5 }}>
          {rule.rule_text}
        </Typography>

        {/* Superseded rule diffs (EXTEND/MERGE) */}
        {supersededRules.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.75 }}
            >
              Superseded {supersededRules.length === 1 ? 'rule' : 'rules'} ({supersededRules.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {supersededRules.map((superseded) => (
                <Box key={superseded.id}>
                  {superseded.found === false ? (
                    <Typography variant="caption" sx={{ color: '#f87171', fontStyle: 'italic' }}>
                      Rule {superseded.id} could not be located (may have been deleted).
                    </Typography>
                  ) : (
                    <JsonDiffViewer
                      mode="text"
                      oldConfig={superseded.rule_text || ''}
                      newConfig={rule.rule_text || ''}
                      oldLabel="Previous rule"
                      newLabel="New rule"
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', my: 1.5 }} />

        {/* Contributors */}
        <Box sx={{ mb: occurrences.length > 0 ? 1.5 : 0 }}>
          <Typography
            variant="caption"
            sx={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.75 }}
          >
            Contributors {contributors.length > 0 ? `(${contributors.length})` : ''}
          </Typography>
          {contributors.length === 0 ? (
            <Typography variant="caption" sx={{ color: '#475569' }}>
              No contributors recorded.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {contributors.map((contributor, idx) => {
                const style = CONTRIBUTOR_LABELS[contributor.contribution_type] || CONTRIBUTOR_LABELS.manual;
                return (
                  <Box
                    key={`${contributor.reviewed_by}-${contributor.review_signal_id || idx}`}
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexWrap: 'wrap' }}
                  >
                    <Chip
                      icon={<PersonIcon sx={{ color: `${style.color} !important`, fontSize: '0.875rem' }} />}
                      label={contributor.reviewed_by}
                      size="small"
                      clickable
                      onClick={() => handleContributorClick(contributor)}
                      sx={{
                        height: 22,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: style.color,
                        background: style.bg,
                        border: `1px solid ${style.color}33`,
                        '&:hover': { background: `${style.color}22` },
                      }}
                    />
                    <Chip
                      label={style.label}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        color: '#94a3b8',
                        background: 'transparent',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                      }}
                    />
                    {contributor.feedback_reason && (
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1.6 }}>
                        {truncate(contributor.feedback_reason)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Occurrence references — deep link into /anomalies when evidence is available */}
        {occurrences.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.75 }}
            >
              Evidence occurrences ({occurrences.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {occurrences.map((occ, idx) => {
                const canLink = !!(occ.evidence_available && occ.occurrence_id && occ.anomaly_id);
                const deepLinkParams = new URLSearchParams({
                  occurrence_id: occ.occurrence_id || '',
                  anomaly_id: occ.anomaly_id || '',
                });
                if (occ.evidence_index !== null && occ.evidence_index !== undefined) {
                  deepLinkParams.set('evidence_index', occ.evidence_index);
                }
                const label = occ.reason
                  ? truncate(occ.reason)
                  : (occ.occurrence_id || occ.anomaly_id || 'occurrence');
                const content = (
                  <Box
                    key={occ.review_signal_id || occ.occurrence_id || idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 0.75,
                      p: 0.75,
                      borderRadius: 1,
                      background: 'rgba(148, 163, 184, 0.06)',
                      border: '1px solid rgba(148, 163, 184, 0.15)',
                      textDecoration: 'none',
                      ...(canLink && {
                        cursor: 'pointer',
                        '&:hover': { background: 'rgba(148, 163, 184, 0.14)' },
                      }),
                    }}
                  >
                    {occ.evidence_available ? (
                      <DescriptionOutlinedIcon sx={{ fontSize: '0.9rem', color: '#64748b', mt: '2px', flexShrink: 0 }} />
                    ) : (
                      <LinkOffIcon sx={{ fontSize: '0.9rem', color: '#64748b', mt: '2px', flexShrink: 0 }} />
                    )}
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', lineHeight: 1.5 }}>
                      {label}
                    </Typography>
                  </Box>
                );
                return canLink ? (
                  <Link
                    key={occ.review_signal_id || occ.occurrence_id || idx}
                    to={`/anomalies?${deepLinkParams.toString()}`}
                    style={{ textDecoration: 'none' }}
                  >
                    {content}
                  </Link>
                ) : content;
              })}
            </Box>
          </Box>
        )}
      </Box>

      {contributorModalOpen && (
        <ContributorFeedbackModal
          isOpen={contributorModalOpen}
          onClose={() => setContributorModalOpen(false)}
          reviewedBy={contributorModalData.reviewedBy}
          entries={contributorModalData.entries}
          loading={contributorModalData.loading}
          error={contributorModalData.error}
        />
      )}
    </>
  );
}

export default DecisionCard;
